/**
 * @erp/workflows - Core workflow engine types and utilities
 */

// Export types
export * from "./types";
export * from "./utils";

// Export validation utilities
export * from "./validation";

// Portable workflow bundle format, shared by the editor's import/export and
// the local workflow CLI.
export * from "./portability";

// Export socket expression engine (workflow closures)
export * from "./expressions";
export * from "./expressionMaterialization";
export * from "./socketExpressionDerivation";
export * from "./manualOverrideValidation";
export * from "./transforms";

// Export registry
export * from "./registry";
export * from "./execution-lifecycle";
export * from "./workflow-priority";
export {
  splitChangelog,
  formatChangelog,
  MAX_CHANGELOG_CHARS,
  normalizeWorkflowEditTrail,
} from "./changelog";

// Scheduled-workflow health check (shared by the FE list indicator + the BE
// detectMissedScheduledWorkflows alert job). Pure logic — no cron-parser — so
// it is safe to bundle into the Temporal workflow sandbox via this barrel.
export * from "./schedule-health";

// Export event schemas
export * from "./events/system-event-schemas";

// Export steps
export * from "./steps/event-trigger.step";
export * from "./steps/email-rule-trigger.step";
export * from "./steps/action-input.step";
export * from "./steps/schedule-trigger.step";
export * from "./steps/http-trigger.step";
export * from "./steps/form-trigger.step";
export * from "./steps/watch-for-file-changes.step";
export * from "./steps/background-agent.step";

// AI steps
export * from "./steps/ai/ai-reasoning.step";
export * from "./steps/ai/verify-step-output.step";
export * from "./steps/ai/entity-resolver.step";
export * from "./steps/ai/identify-customer.step";
export * from "./steps/ai/identify-supplier.step";
export * from "./steps/ai/eligible-entity-ids";
export * from "./steps/ai/identify-location.step";
export * from "./steps/ai/voice-agent.step";
export * from "./steps/ai/web-browser.step";
export * from "./steps/ai/login-to-remote-desktop.step";
export * from "./steps/ai/remote-desktop-execute.step";
export * from "./steps/ai/close-remote-desktop.step";
export * from "./steps/ai/detect-language.step";
export * from "./steps/ai/segment-language.step";
export * from "./steps/ai/translate-language.step";

// Email routing steps

// Communications steps
export * from "./steps/communications";

// AP invoice steps
export * from "./steps/accounts-payable/invoices/extract-ap-invoice-from-email";
export * from "./steps/accounts-payable/invoices/extract-ap-invoice-from-document.step";
export * from "./steps/accounts-payable/invoices/create-ap-invoice-draft.step";
export * from "./steps/accounts-payable/invoices/get-ap-invoice.step";
export * from "./steps/accounts-payable/invoices/update-ap-invoice-field.step";
export * from "./steps/data-access/decompose-entity.step";
export * from "./entity-decomposition";
export * from "./steps/accounts-payable/invoices/match-invoice-line-items.step";

// AP payment steps
export * from "./steps/accounts-payable/payments/schedule-payment.step";

// AR sales order draft steps
export * from "./steps/accounts-receivable/sales-order-drafts/customer-resolution-mode";
export * from "./steps/accounts-receivable/sales-order-drafts/create-sales-order-draft.step";
export * from "./steps/accounts-receivable/sales-order-drafts/create-draft-sales-order.step";
export * from "./steps/accounts-receivable/sales-order-drafts/create-sales-order-draft-from-document.step";
export * from "./steps/accounts-receivable/sales-order-drafts/split-staged-po.step";
export * from "./steps/accounts-receivable/sales-order-drafts/split-draft-sales-order.step";
export * from "./steps/accounts-receivable/sales-order-drafts/compute-request-date.step";
export * from "./steps/accounts-receivable/sales-order-drafts/detect-order-issues.step";
export * from "./steps/accounts-receivable/sales-order-drafts/compute-order-facts.step";
export * from "./steps/accounts-receivable/sales-order-drafts/resolve-order-fields.step";
export * from "./steps/accounts-receivable/sales-order-drafts/resolve-draft-details.step";
export * from "./steps/accounts-receivable/sales-order-drafts/reassign-draft-sales-order.step";
export * from "./steps/accounts-receivable/sales-order-drafts/classify-organization.step";
export * from "./steps/accounts-receivable/sales-order-drafts/submit-draft-sales-orders.step";
export * from "./steps/accounts-receivable/discounts/set-discount-group-status.step";
export * from "./steps/accounts-receivable/discounts/create-discount-group-from-extraction.step";
export * from "./steps/accounts-receivable/sales-order-drafts/submit-sales-orders.step";
export * from "./steps/accounts-receivable/sales-order-drafts/await-erp-order-numbers.step";

// AR remittance steps
export * from "./steps/accounts-receivable/remittances";

// AR cash receipt steps
export * from "./steps/accounts-receivable/cash-receipts";

// AR collection-thread reminder steps
export * from "./steps/accounts-receivable/collection-reminders";

// AR collection-thread summary (digest email) steps
export * from "./steps/accounts-receivable/collection-summary";

// AR collection evidence filing steps
export * from "./steps/accounts-receivable/collection-evidence";

// AR task-queue scan steps (summary dashboard work queues)
export * from "./steps/accounts-receivable/ar-tasks";

// Control flow steps
export * from "./steps/control-flow";

// Famous integration steps
export * from "./steps/integrations/famous/MatchSupplierWithFamousStep";
export * from "./steps/integrations/famous/SyncAPInvoiceToFamousStep";
export * from "./steps/integrations/famous/fetch-purchase-order-from-famous.step";
export * from "./steps/integrations/famous/fetch-sales-order-from-famous.step";
export * from "./steps/integrations/famous/fetch-transfer-order-from-famous.step";
export * from "./steps/integrations/famous/ingest-sales-load.step";
export * from "./steps/integrations/famous/ingest-edi-translation-lists.step";
export * from "./steps/integrations/famous/extract-famous-order-from-email-subject.step";
export * from "./steps/integrations/famous/send-famous-shipment-to-orderful-856.step";
export * from "./steps/integrations/famous/send-famous-invoice-to-orderful.step";
export * from "./steps/integrations/famous/send-famous-order-confirmation-to-orderful-855.step";
export * from "./steps/integrations/famous/process-pick-ticket-bol.step";
export * from "./steps/integrations/famous/ingest-ar-data.step";
export * from "./steps/integrations/famous/ingest-ar-payments.step";
export * from "./steps/integrations/famous/famous-sql-query.step";
export * from "./steps/integrations/famous/write-file-to-famous.step";

// Task steps
export * from "./steps/tasks/shared";
export * from "./steps/tasks/create-task.step";
export * from "./steps/tasks/create-typed-task.step";
export * from "./steps/tasks/generate-task.step";
export * from "./steps/tasks/reassign-task.step";
export * from "./steps/tasks/resolve-task.step";
export * from "./steps/tasks/create-subtask.step";
export * from "./steps/tasks/complete-task.step";
export * from "./steps/tasks/sync-tasks-from-rows.step";

// Shipping exception steps
export * from "./steps/shipping";

// Utility steps
export * from "./steps/utility/hello-world.step";
export * from "./steps/utility/random-number-generator.step";
export * from "./steps/utility/log-value.step";
export * from "./steps/utility/array-length.step";
export * from "./steps/utility/throw-error.step";
export * from "./steps/utility/platform-heartbeat.step";
export * from "./steps/utility/code-sandbox.step";
export * from "./steps/utility/code-sandbox-templates";

// Ship and Debit steps
export * from "./steps/ship-and-debit";

// Procurement steps
export * from "./steps/procurement";

// Short Code steps
export * from "./steps/short-codes";

// Oracle Fusion integration steps
export * from "./steps/integrations/oracle-fusion/fusion-sync.step";

// Jira integration steps
export * from "./steps/integrations/jira/create-jira-issue.step";

// Microsoft Teams integration steps
export * from "./steps/integrations/microsoft-teams/send-teams-notification.step";

// Generic external HTTP integration step (registry-driven or inline)
export * from "./steps/integrations/external-http-call.step";

// Pricing steps (SPA change request workflow)
export * from "./steps/pricing/split-845-contracts.step";
export * from "./steps/pricing/map-845-to-spa.step";
export * from "./steps/pricing/upsert-spa.step";
export * from "./steps/pricing/upsert-spa-source.step";
export * from "./steps/pricing/list-spa-sources.step";
export * from "./steps/pricing/email-run-summary.step";
export * from "./steps/pricing/spa-renewal-read-quotes.step";
export * from "./steps/pricing/spa-renewal-read-as400-loads.step";
export * from "./steps/pricing/spa-renewal-build-load-sheet.step";

// Rebates steps
export * from "./steps/rebates/run-rebate-forecast.step";
export * from "./steps/rebates/create-pye-snapshot.step";
export * from "./steps/rebates/rebate-csv-extract-constants.step";
export * from "./steps/rebates/rebate-csv-preprocess.step";
export * from "./steps/rebates/rebate-csv-map-fields.step";
export * from "./steps/rebates/rebate-csv-transform.step";
export * from "./steps/rebates/enqueue-rebate-session.step";

// Shared step field helpers
export * from "./steps/csv-char-field";
export * from "./steps/data-source-link-selection";

// Data Access steps (generic pipeline)
export * from "./steps/data-access/join-documents.step";
export * from "./steps/data-access/blob-storage-fetch.step";
export * from "./steps/data-access/data-asset.step";
export * from "./steps/data-access/core-enum-fanout.step";
export * from "./steps/data-access/pipeline-rows-insert.step";
export * from "./steps/data-access/pipeline-rows-query.step";
export * from "./steps/data-access/pipeline-rows-update.step";
export * from "./steps/data-access/master-data-bulk-insert.step";
export * from "./steps/data-access/stateful-table-write.step";
export * from "./steps/data-access/stateful-table-lookup-row.step";
export * from "./steps/data-access/use-data-mapping.step";
export * from "./steps/data-access/stateful-table-read-frame.step";
export * from "./steps/data-access/observe-data-source-link.step";

// Workflow control steps
export * from "./steps/workflows/snooze-workflow-rerun.step";

// Shared step field helpers
export * from "./steps/csv-char-field";

// Data Ingest (ETL) step types + default execution timeout
export * from "./steps/data-ingest-consts";

// Browser Use Agent step types + default execution timeout
export * from "./steps/browser-agent-consts";

// DataSource Writer steps
export * from "./steps/datasource-writer/csv-datasource-writer.step";
export * from "./steps/datasource-writer/fwf-datasource-writer.step";

// DataSource Adapter steps
export * from "./steps/fwf-shared";
export * from "./steps/datasource-adapter/aws-s3-datasource-adapter.step";
export * from "./steps/datasource-adapter/blob-storage-datasource-adapter.step";

// Transformation steps (generic pipeline)
export * from "./steps/transformation/parse-file.step";
export * from "./steps/transformation/parse-edi.step";
export * from "./steps/transformation/pipeline-rows-process.step";
export * from "./steps/transformation/classify-master-data.step";
export * from "./steps/transformation/transform-e844d-fields.step";
export * from "./steps/transformation/split-document-v2.step";
export * from "./steps/transformation/filter-attachments.step";
export * from "./steps/transformation/csv-transform.step";
export * from "./steps/transformation/csv-transform-v4.step";
export * from "./steps/transformation/managed-mapper.step";
export * from "./steps/transformation/map-value.step";
export * from "./steps/transformation/render-document-as-text.step";

// Memory bank steps (workflow-scoped memories)
export * from "./steps/memory-bank/store-memory.step";
export * from "./steps/memory-bank/retrieve-memories.step";
export * from "./steps/item-master";
export * from "./steps/accounts-payable/suppliers/upsert-vendor.step";
export * from "./steps/accounts-receivable/customers/upsert-customer.step";
export * from "./steps/accounts-receivable/customers/match-customer-with-evidence.step";
export * from "./steps/accounts-payable/suppliers/upsert-vendor-rexel.step";
export * from "./steps/accounts-payable/suppliers/supplier-document-line-match";
export * from "./steps/accounts-payable/suppliers/match-supplier-document-lines.step";
export * from "./steps/accounts-payable/suppliers/write-supplier-product-matches.step";

export * from "./steps/utility/workbook-transform.step";
export * from "./steps/workbooks/run-workbook.step";
